import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db, TrainingDay } from '../database';
import { seedTrainingDays } from '../utils/seedData';

interface TrainingDayWithExerciseCount extends TrainingDay {
  exerciseCount: number;
}

const TrainingDays: React.FC = () => {
  const [trainingDays, setTrainingDays] = useState<TrainingDayWithExerciseCount[]>([]);

  useEffect(() => {
    loadTrainingDays();
  }, []);

  const loadTrainingDays = async () => {
    try {
      const days = await db.trainingDays.orderBy('name').toArray();
      
      // Load exercise counts for each training day
      const daysWithCounts = await Promise.all(
        days.map(async (day) => {
          const exerciseCount = await db.trainingDayExercises
            .where('trainingDayId')
            .equals(day.id!)
            .count();
          
          return {
            ...day,
            exerciseCount
          };
        })
      );
      
      setTrainingDays(daysWithCounts);
    } catch (error) {
      console.error('Failed to load training days:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this training day?')) {
      try {
        await db.trainingDays.delete(id);
        await db.trainingDayExercises.where('trainingDayId').equals(id).delete();
        await loadTrainingDays();
      } catch (error) {
        console.error('Failed to delete training day:', error);
      }
    }
  };

  const handleSeedTrainingDays = async () => {
    try {
      await seedTrainingDays();
      await loadTrainingDays(); // Refresh the page
    } catch (error) {
      console.error('Failed to seed training days:', error);
      alert('Failed to seed training days. Check console for details.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">
          Training Days
        </h2>
        <Link
          to="/training-days/new"
          className="bg-blue-600 text-white px-3 py-2 rounded text-sm"
        >
          New Training Day
        </Link>
      </div>

      {trainingDays.length === 0 ? (
        <div className="text-center text-gray-500 py-8 space-y-4">
          <p>No training days yet. Create your first training day!</p>
          <button
            onClick={handleSeedTrainingDays}
            className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            📝 Load Sample Training Days
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {trainingDays.map((day) => (
            <div
              key={day.id}
              className="bg-white border rounded-lg p-4"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{day.name}</h3>
                  <p className="text-gray-600 text-sm">
                    {day.exerciseCount} {day.exerciseCount === 1 ? 'exercise' : 'exercises'}
                  </p>
                </div>
                <div className="flex space-x-2 ml-4">
                  <Link
                    to={`/training-days/${day.id}`}
                    className="text-blue-600 text-sm"
                  >
                    Edit
                  </Link>
                  <button
                    onClick={() => handleDelete(day.id!)}
                    className="text-red-600 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TrainingDays;